import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Coins, AlertTriangle, CheckCircle2, Lock, Unlock } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { fmt, fmtDate, gbErr } from './gbAdminUtils';

const STATUS = {
  PENDING: { txt: 'Na fila', cls: 'bg-yellow-100 text-yellow-800' },
  PROCESSING: { txt: 'Creditando', cls: 'bg-blue-100 text-blue-800' },
  FAILED: { txt: 'Tentando de novo', cls: 'bg-orange-100 text-orange-800' },
  STUCK: { txt: 'Parado — precisa de você', cls: 'bg-red-100 text-red-800' },
};

export default function GbWithdrawals() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/admin/gb/withdrawals/fulfillment');
      setData(d);
    } catch (e) { toast.error(gbErr(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (id) => {
    setBusy(id || 'all');
    try {
      const { data: r } = await api.post('/admin/gb/withdrawals/retry', { withdraw_id: id || null });
      toast.success(`${r.requeued} resgate(s) na fila · ${r.delivered} entregue(s)`);
      await load();
    } catch (e) { toast.error(gbErr(e)); } finally { setBusy(null); }
  };

  const virarChave = async (ligar) => {
    if (!ligar && !window.confirm('Desligar os resgates? Os jogadores não vão conseguir pedir '
      + 'resgate até você ligar de novo. O que já está na fila continua sendo entregue.')) return;
    setBusy('chave');
    try {
      await api.post('/admin/gb/withdrawals/toggle', { enabled: ligar });
      toast.success(ligar ? 'Resgates liberados para os jogadores.'
        : 'Resgates desligados — ninguém consegue pedir agora.');
      await load();
    } catch (e) { toast.error(gbErr(e)); } finally { setBusy(null); }
  };

  if (!data) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  const travados = data.stuck_total || 0;
  const ligado = data.enabled !== false;

  return (
    <div className={`space-y-4 rounded-xl border p-4 ${travados ? 'border-red-400 bg-red-50/60' : 'border-emerald-300 bg-emerald-50/40'}`}
      data-testid="gb-admin-withdrawals">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <Coins className="h-4 w-4 text-amber-500" />Resgates de moedas amarelas
          {travados > 0 && <Badge className="bg-red-600 text-white" data-testid="gb-withdrawals-badge">{travados}</Badge>}
        </h4>
        {travados > 0 && (
          <Button size="sm" className="bg-red-600 text-white hover:bg-red-700"
            disabled={busy !== null} onClick={() => retry(null)} data-testid="gb-withdrawals-retry-all">
            {busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reprocessar todos'}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={load} data-testid="gb-withdrawals-refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-3 ${
        ligado ? 'border-emerald-300 bg-white' : 'border-amber-400 bg-amber-50'}`}
        data-testid="gb-withdrawals-switch">
        <div className="flex items-center gap-3">
          {ligado ? <Unlock className="h-5 w-5 text-emerald-600" /> : <Lock className="h-5 w-5 text-amber-600" />}
          <div>
            <p className="text-sm font-bold text-slate-800">
              Resgates de moedas amarelas: {ligado ? 'LIBERADOS' : 'DESLIGADOS'}
            </p>
            <p className="text-[11px] text-slate-600">
              {ligado
                ? 'Os jogadores podem pedir resgate do saldo para a conta KAKO.'
                : 'Ninguém consegue pedir resgate (bloqueio no servidor). O que já está na fila continua sendo entregue.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={ligado} disabled={busy !== null}
            onCheckedChange={(v) => virarChave(!!v)}
            data-testid="gb-withdrawals-toggle" />
          <span className="text-xs font-bold text-slate-700">
            {busy === 'chave' ? 'salvando…' : ligado ? 'ligado' : 'desligado'}
          </span>
        </div>
      </div>

      <p className="text-xs text-slate-600">
        O cliente pede o resgate, o servidor debita o saldo dele na hora e a entrega na conta KAKO entra
        nesta fila. Se a KAKO recusar (saldo zerado, por exemplo), o sistema tenta de novo sozinho e o
        resgate aparece aqui em vermelho. O crédito nunca sai duas vezes (conferimos a transferência
        na KAKO antes de qualquer reenvio) e só sai até o saldo de moedas daquele cliente.
        Saldo KAKO agora: <b data-testid="gb-withdrawals-kako-balance">{fmt(data.kako_balance)}</b> moedas.
      </p>

      {data.fund && (
        <p className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold ${
          data.lastro_ok === false ? 'border-red-400 bg-red-100 text-red-700' : 'border-emerald-300 bg-emerald-50 text-emerald-700'}`}
          data-testid="gb-withdrawals-lastro">
          {data.lastro_ok === false ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {data.lastro_ok === false
            ? 'ATENÇÃO: há saldo de moedas sem lastro no fundo do evento — confira a auditoria.'
            : `Todo saldo de moedas dos jogadores está lastreado no fundo (reservado ${fmt(data.fund.reserved)}).`}
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
        <div className="rounded-lg border bg-white p-2" data-testid="gb-withdrawals-queued">
          <p className="text-[10px] uppercase text-slate-400">na fila</p>
          <p className="text-lg font-black text-slate-800">{data.queued || 0}</p>
        </div>
        <div className="rounded-lg border bg-white p-2" data-testid="gb-withdrawals-stuck">
          <p className="text-[10px] uppercase text-slate-400">sem chegar na conta</p>
          <p className={`text-lg font-black ${travados ? 'text-red-600' : 'text-emerald-600'}`}>{travados}</p>
        </div>
        <div className="rounded-lg border bg-white p-2">
          <p className="text-[10px] uppercase text-slate-400">moedas paradas</p>
          <p className="text-lg font-black text-slate-800">{fmt(data.stuck_coins)}</p>
        </div>
        <div className="rounded-lg border bg-white p-2" data-testid="gb-withdrawals-review">
          <p className="text-[10px] uppercase text-slate-400">parados de vez</p>
          <p className={`text-lg font-black ${data.manual_review ? 'text-red-600' : 'text-emerald-600'}`}>{data.manual_review || 0}</p>
        </div>
        <div className="rounded-lg border bg-white p-2">
          <p className="text-[10px] uppercase text-slate-400">entregues</p>
          <p className="text-lg font-black text-emerald-600">{data.delivered || 0}</p>
        </div>
      </div>

      {(data.items || []).length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="gb-withdrawals-ok">
          <CheckCircle2 className="h-4 w-4" />Todo resgate pedido já está na conta do cliente.
        </p>
      ) : (
        <div className="space-y-2" data-testid="gb-withdrawals-list">
          {data.items.map((w) => {
            const st = STATUS[w.status] || STATUS.PENDING;
            return (
              <div key={w.withdraw_id} data-testid={`gb-withdrawal-${w.withdraw_id.slice(0, 8)}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900">
                    {fmt(w.coins)} moedas · KAKO {w.kako_id}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {fmtDate(w.created_at)} · tentativas: {w.attempts || 0}
                    {w.fail_reason ? ` · ${w.fail_reason}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {w.status === 'STUCK' && <AlertTriangle className="h-4 w-4 text-red-600" />}
                  <Badge className={st.cls}>{st.txt}</Badge>
                  <Button size="sm" variant="outline" disabled={busy !== null}
                    onClick={() => retry(w.withdraw_id)}
                    data-testid={`gb-withdrawal-retry-${w.withdraw_id.slice(0, 8)}`}>
                    {busy === w.withdraw_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Tentar agora'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
